import { forwardRef } from 'react';
import type { PropsWithoutRef, ReactNode } from 'react';
import styled, { css } from 'styled-components';

import type { NoChildrenProp, RefElement, WithAttributes, HeadingTag } from '../../types';
import { defaultThemeProp } from '../../theme';
import { useDirection, useI18n, useUID } from '../../hooks';
import HiddenText from '../HiddenText';
import { calculateFontSize, type FontSize } from '../../styles';
import Button from '../Button';
import type { ButtonProps } from '../Button';
import Flex from '../Flex';
import Icon, { StyledIcon, registerIcon } from '../Icon';
import * as caretRightIcon from '../Icon/icons/caret-right.icon';
import Text from '../Text';
import Count from '../Badges/Count';
import type { CountProps } from '../Badges/Count';

registerIcon(caretRightIcon);

export type ExpandCollapseButtonProps = WithAttributes<
  'button',
  NoChildrenProp &
    Pick<ButtonProps, 'compact'> & {
      /** Indicates whether the content is expanded. */
      expanded: boolean;

      /** Callback function triggered when the button is toggled. */
      onToggle: () => void;

      /**
       * Label for the button. Can be a string or an object containing text and a heading tag.
       */
      label?: string | { text: string; headingTag: HeadingTag };

      /**
       * If true, only the icon is displayed. Tooltip will always show "Expand" or "Collapse".
       * @default false
       */
      iconOnly?: boolean;

      /** Optional count to display next to the label. Only available when iconOnly is false. */
      count?: CountProps['children'] | CountProps;
    }
>;

export const StyledExpandCollapseButton = styled(Button)<{
  $iconOnly?: boolean;
  $headingTag?: HeadingTag;
}>(({ $iconOnly, $headingTag, theme }) => {
  const { rtl } = useDirection();
  const fontSize = calculateFontSize(theme.base['font-size'], theme.base['font-scale']);

  return css`
    ${!$iconOnly &&
    css`
      font-size: ${fontSize[
        theme.components.text[$headingTag ?? 'primary']['font-size'] as FontSize
      ]};
    `}

    ${StyledIcon} {
      transition: transform ${theme.base.animation.speed} ${theme.base.animation.timing.ease};
    }

    &[aria-expanded='true'] ${StyledIcon} {
      transform: rotate(90deg);
    }

    &[aria-expanded='false'] ${StyledIcon} {
      transform: rotate(${rtl ? 180 : 0}deg);
    }
  `;
});

StyledExpandCollapseButton.defaultProps = defaultThemeProp;

export default forwardRef<
  RefElement<ExpandCollapseButtonProps>,
  PropsWithoutRef<ExpandCollapseButtonProps>
>(function ExpandCollapseButton(
  {
    expanded,
    onToggle,
    label,
    iconOnly = false,
    count,
    compact,
    'aria-labelledby': labelledBy,
    ...restProps
  },
  ref
) {
  const t = useI18n();
  const hiddenTextId = useUID();
  const toggleText = expanded ? t('collapse') : t('expand');

  let ariaLabel: string | undefined;
  let ariaLabelledBy: string | undefined;

  if (labelledBy && !label) {
    ariaLabelledBy = `${hiddenTextId} ${labelledBy}`;
  } else if (iconOnly) {
    if (label) {
      const labelText = typeof label === 'string' ? label : label.text;
      ariaLabel = `${toggleText} - ${labelText}`;
    } else {
      ariaLabel = toggleText;
    }
  }

  const tooltipText = iconOnly ? toggleText : undefined;

  let renderedLabel: ReactNode;
  let headingTag: HeadingTag | undefined;
  if (iconOnly || !label) {
    renderedLabel = null;
  } else if (typeof label === 'string') {
    renderedLabel = <span>{label}</span>;
  } else {
    renderedLabel = <Text variant={label.headingTag}>{label.text}</Text>;
    headingTag = label.headingTag;
  }

  let renderedCount: ReactNode;
  if (iconOnly || count === undefined) {
    renderedCount = null;
  } else if (typeof count === 'number' || count === null) {
    renderedCount = <Count>{count}</Count>;
  } else {
    renderedCount = <Count {...count} />;
  }

  return (
    <StyledExpandCollapseButton
      {...restProps}
      ref={ref}
      onClick={onToggle}
      aria-expanded={expanded}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      label={tooltipText}
      icon={iconOnly}
      compact={compact}
      variant={iconOnly ? 'simple' : 'text'}
      $iconOnly={iconOnly}
      $headingTag={headingTag}
    >
      {labelledBy && !label && <HiddenText id={hiddenTextId}>{`${toggleText} - `}</HiddenText>}
      <Flex container={{ alignItems: 'center', gap: 1 }} as='span'>
        <Icon name='caret-right' size={!iconOnly ? 'font-size' : undefined} />
        {renderedLabel}
        {renderedCount}
      </Flex>
    </StyledExpandCollapseButton>
  );
});
